/**
 * Auth endpoints (T025). All use `retryOnUnauthorized: false` — the auth
 * bootstrap (useAuth, T026) controls the refresh flow explicitly, and a 401 on
 * login/verify is a real error, not a stale-token retry.
 */
import { api } from '../http'
import {
  LoreOrganizationOperationSchema,
  DesktopAuthorizeResponseSchema,
  DesktopLoginPreviewResponseSchema,
  UserProfileResponseSchema,
  LoginResponseSchema,
  SeedUsersResponseSchema,
} from '../schemas'

const noRetry = { retryOnUnauthorized: false } as const

export const getMe = () => api.get('/me', UserProfileResponseSchema, noRetry)

/** Dev-only seed accounts for one-click login. Empty list on real deployments. */
export const getSeedUsers = () =>
  api.get('/dev/seed-users', SeedUsersResponseSchema, noRetry)

export const login = (email: string, password: string) =>
  api.post('/auth/login', { email, password }, LoginResponseSchema, noRetry)

export const register = (email: string, password: string, name: string) =>
  api.post('/auth/register', { email, password, name }, undefined, noRetry)

export const verifyEmail = (email: string, code: string) =>
  api.post('/auth/verify-email', { email, code }, undefined, noRetry)

export const resendVerification = (email: string) =>
  api.post('/auth/resend-verification', { email }, undefined, noRetry)

export const logout = () =>
  api.post('/auth/logout', undefined, undefined, noRetry)

/** Shared single-flight refresh (dedups with the on-401 retry). */
export const refresh = () => api.refresh()

export const completeLoreSession = (
  sessionCode: string,
  clientState: string,
  organizationId: string,
) =>
  api.post(
    '/lore/auth/sessions/complete',
    {
      session_code: sessionCode,
      client_state: clientState,
      organization_id: organizationId,
    },
    undefined,
    noRetry,
  )

export const authorizeDesktopSession = (
  attemptId: string,
  state: string,
  organizationId: string | null,
) =>
  api.post(
    '/desktop-auth/authorize',
    {
      attempt_id: attemptId,
      state,
      organization_id: organizationId,
    },
    DesktopAuthorizeResponseSchema,
    noRetry,
  )

export const previewDesktopSession = (attemptId: string, state: string) =>
  api.post(
    '/desktop-auth/preview',
    {
      attempt_id: attemptId,
      state,
    },
    DesktopLoginPreviewResponseSchema,
    noRetry,
  )

export const previewLoreOrganizationOperation = (code: string) =>
  api.post(
    '/lore/organization-operations/preview',
    { code },
    LoreOrganizationOperationSchema,
    noRetry,
  )

export const confirmLoreOrganizationOperation = (code: string) =>
  api.post(
    '/lore/organization-operations/confirm',
    { code },
    LoreOrganizationOperationSchema,
    noRetry,
  )
